//==============================================================================
// Test Locator
//
// Displays nearby testers to the user's geolocation.
//==============================================================================
import * as React from 'react';
import classnames from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react';

import { clsHelper } from '../../utilities/class-name-helper';
import { varReplace } from '../../utilities/variable-replacer';
import { TestType } from '../../utilities/test-constants';

import { RichTextComponent } from '@msdyn365-commerce/core';
import { Button, Heading } from '@msdyn365-commerce-modules/utilities';

import { searchByLocationAsync } from '../../actions/DataActionExtension.g';
import { IAdministratorLocationCriteria, IAdministratorEntity } from '../../actions/DataServiceEntities.g';

import { ITestLocatorData } from './test-locator.data';
import { ITestLocatorProps } from './test-locator.props.autogenerated';
import TestLocatorContact from './components/test-locator-contact';
import TestLocatorMap from './components/test-locator-map';

//==============================================================================
// INTERFACES
//==============================================================================
export interface FormData {
    testType: string;
    testLocation: string;
    testRadius: string;
}

//==============================================================================
// CLASS NAME UTILITY
//==============================================================================
const BASE_CLASS = 'test-locator';
const cls = (fragment?: string) => clsHelper(BASE_CLASS, fragment);

//==============================================================================
// CLASS DEFINITION
//==============================================================================
/**
 * TestLocator component
 * @extends {React.Component<ITestLocatorProps<ITestLocatorData>>}
 */
//==============================================================================
@observer
class TestLocator extends React.Component<ITestLocatorProps<ITestLocatorData>> {
    //==========================================================================
    // VARIABLES
    //==========================================================================
    @observable private isSubmitting: boolean = false;
    @observable private formSuccess: boolean = false;
    @observable private formFailure: boolean = false;
    @observable private result?: IAdministratorEntity[];
    @observable private selectedAdmin?: IAdministratorEntity;

    @observable private formData: FormData = {
        testType: TestType.iowa,
        testLocation: '',
        testRadius: '25'
    };

    private testRadius: string[] = ['10', '25', '50', '100', '150','200', '250'];
    private directionsUrl: string = `https://maps.google.com/maps?q={address}`;

    //==========================================================================
    // PUBLIC METHODS
    //==========================================================================
    //------------------------------------------------------
    // Render function
    //------------------------------------------------------
    public render(): JSX.Element | null {
        const { config, resources } = this.props;

        return (
            <div className={classnames(BASE_CLASS, config.className)}>
                {/* Do not render form after successful search */}
                {!this.formSuccess && (
                    <div className={cls('form')}>
                        {config.heading && <Heading className={cls('heading')} headingTag={config.heading.tag} text={config.heading.text} />}
                        {config.description && <RichTextComponent text={config.description} className={cls('description')} />}

                        <div className={cls('type')}>
                            <h6 className={cls('type-heading')}>{resources.testLocator_typeHeading}</h6>
                            <div className={cls('type-description')}>{resources.testLocator_typeDescription}</div>
                            <div className={classnames(cls('type-form'), cls('form-section'))}>
                                <div className={classnames(cls('input-group'), 'type', cls('radio'))}>
                                    <label className={cls('radio-label')}>
                                        <input
                                            id='testType'
                                            className={cls('radio-input')}
                                            type='radio'
                                            aria-label={resources.testLocator_typeIowaFull}
                                            value={resources.testLocator_typeIowa}
                                            checked={this.formData.testType === TestType.iowa && true}
                                            onChange={this._onInputChange}
                                        />
                                        {resources.testLocator_typeIowaFull}
                                    </label>
                                    <label className={cls('radio-label')}>
                                        <input
                                            id='testType'
                                            className={cls('radio-input')}
                                            type='radio'
                                            aria-label={resources.testLocator_typeStanfordFull}
                                            value={resources.testLocator_typeStanford}
                                            checked={this.formData.testType === TestType.stanford && true}
                                            onChange={this._onInputChange}
                                        />
                                        {resources.testLocator_typeStanfordFull}
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className={cls('search')}>
                            <h6 className={cls('search-heading')}>{resources.testLocator_searchHeading}</h6>
                            <div className={cls('search-description')}>{resources.testLocator_searchDescription}</div>
                            <div className={classnames(cls('search-form'), cls('form-section'))}>
                                <div className={classnames(cls('input-group'), 'location')}>
                                    <label className={cls('label')} htmlFor='test-location'>{resources.testLocator_searchLocation}</label>
                                    <input
                                        id='testLocation'
                                        className={cls('input')}
                                        aria-label={resources.testLocator_searchLocation}
                                        value={this.formData.testLocation}
                                        onChange={this._onInputChange}
                                    />
                                </div>
                                {this._renderInputRadius(this._onInputChange)}
                            </div>
                        </div>

                        <div className={cls('buttons')}>
                            <Button
                                className={classnames(cls('button'), {'disabled': this.isSubmitting})}
                                aria-label={resources.testLocator_searchButton}
                                onClick={() => this._performSearch(this.formData)}
                                disabled={this.isSubmitting}
                            >
                                {resources.testLocator_searchButton}
                            </Button>
                        </div>

                    </div>
                )}

                {/* Render while loading results */}
                {this.isSubmitting && (
                    <div className={cls('loading')}>
                        {resources.testLocator_loadingResults}
                    </div>
                )}

                {/* Render if no results found */}
                {this.formFailure && (
                    <div className={cls('empty')}>
                        {resources.testLocator_noResults}
                    </div>
                )}

                {/* Render results only after successful search */}
                {this.formSuccess && !this.formFailure && (
                    <div className={cls('results')}>
                        {this.result && (
                            <TestLocatorMap cls={cls} moduleProps={this.props} admins={this.result} selectedAdmin={this.selectedAdmin} selectAdmin={this._selectAdmin} />
                        )}
                        <div className={cls('results-heading')}>
                            <div className={cls('results-message')}>
                                <span className={cls('results-text')}>
                                    {varReplace(resources.testLocator_results, {
                                        testNumber: String(this.result?.length),
                                        testType: this.formData.testType,
                                        plural: !!this.result?.length || this.result?.length === 0 ? 's' : '',
                                        searchTerm: this.formData.testLocation
                                    })}
                                </span>
                                <a
                                    className={cls('results-link')}
                                    aria-label={resources.testLocator_modifyLink}
                                    onClick={this._resetResult}
                                    role='button'
                                >
                                    {resources.testLocator_modifyLink}
                                </a>
                            </div>
                            <div className={cls('results-radius')}>
                                {this._renderInputRadius(this._onSelectChangeSubmit)}
                            </div>
                        </div>
                        <ol className={cls('results-list')}>
                            {this.result?.map((admin, index) => {
                                return (
                                    <li
                                        key={index}
                                        className={classnames(cls('admin'), {'selected': admin === this.selectedAdmin})}
                                        onClick={() => this._selectAdmin(admin)}
                                    >
                                        <div className={cls('admin-number')}>
                                            {index + 1}
                                        </div>
                                        <div className={cls('admin-details')}>
                                            <div className={cls('admin-heading')}>
                                                <span className={cls('admin-name')}>
                                                    {admin.Name}
                                                </span>
                                                <span className={cls('admin-distance')}>
                                                    {/* @VO: Distance does not seem to exist but spec has it */}
                                                    {/* @ts-ignore */}
                                                    {/* {admin.Distance} {resources.testLocator_distanceUnit} */}
                                                </span>
                                            </div>
                                            <div className={cls('admin-info')}>
                                                <span className={cls('admin-address')}>
                                                    {admin.LocatorStreet}, {admin.LocatorCity}, {admin.LocatorState} {admin.LocatorZipCode}
                                                </span>
                                                <a
                                                    className={classnames(cls('admin-link'), 'directions')}
                                                    aria-label={resources.testLocator_directionsLink}
                                                    href={varReplace(this.directionsUrl, {
                                                        address: `${admin.LocatorStreet} ${admin.LocatorCity} ${admin.LocatorState} ${admin.LocatorZipCode}`
                                                    })}
                                                    target='_blank'
                                                    rel='noreferrer'
                                                    role='button'
                                                >
                                                    {resources.testLocator_directionsLink}
                                                </a>
                                                <TestLocatorContact cls={cls} resources={resources} admin={admin} />
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                )}
            </div>
        );
    }

    //==========================================================================
    // PRIVATE METHODS
    //==========================================================================
    //------------------------------------------------------
    // Render search radius input
    //------------------------------------------------------
    private readonly _renderInputRadius = (onChange: React.ChangeEventHandler<HTMLSelectElement>): JSX.Element => {
        const { resources } = this.props;

        return (
            <div className={classnames(cls('input-group'), 'radius')}>
                <label className={cls('label')} htmlFor='test-radius'>{resources.testLocator_searchRadius}</label>
                <select
                    id='testRadius'
                    className={cls('select')}
                    aria-label={resources.testLocator_searchRadius}
                    defaultValue={this.formData.testRadius}
                    onChange={onChange}
                >
                    {this.testRadius.map((radius, index) => (
                        <option value={radius} key={index}>{radius} {resources.testLocator_searchRadiusUnit}</option>
                    ))}
                </select>
            </div>
        );
    };

    //------------------------------------------------------
    // On input change
    //------------------------------------------------------
    private readonly _onInputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLTextAreaElement>): void => {
        // @ts-ignore
        this.formData[event.currentTarget.id] = event.currentTarget.value;
    };

    //------------------------------------------------------
    // On select change that triggers form submission
    //------------------------------------------------------
    private readonly _onSelectChangeSubmit = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        this._onInputChange(event);
        this._performSearch(this.formData);
    };

    //------------------------------------------------------
    // Select an admin
    //------------------------------------------------------
    private readonly _selectAdmin = (admin: IAdministratorEntity): void => {
        this.selectedAdmin = admin;
    };

    //------------------------------------------------------
    // Performs geolocation search with search term and
    // radius
    //------------------------------------------------------
    private readonly _performSearch = async (searchData: FormData): Promise<void> => {
        const { context, data } = this.props;
        const { actionContext, request } = context;
        const storeSelectorStateManager = data.storeSelectorStateManager.result;

        if (!storeSelectorStateManager || !searchData.testLocation) {
            this.formFailure = true;
            return;
        }

        if (request) {
            this.isSubmitting = true;
            this.formFailure = false;

            const { testType, testLocation, testRadius } = searchData;
            const testCoordinates = await storeSelectorStateManager.geoLocate(testLocation.trim() !== '' ? testLocation : '0', actionContext);

            const administrator: IAdministratorLocationCriteria = {
                Latitude: testCoordinates?.latitude,
                Longitude: testCoordinates?.longitude,
                Radius: Number(testRadius),
                TestTypes: testType
            };

            searchByLocationAsync({ callerContext: actionContext }, administrator).then(result => {
                if (!!result?.Result?.length) {
                    this.result = result.Result;
                    this.selectedAdmin = result.Result[0];
                    this.formFailure = false;
                    this.formSuccess = true;
                } else {
                    this.formFailure = true;
                    this.formSuccess = false;
                }
            }).catch(error => {
                context.telemetry.error(error.message);
                this.formFailure = true;
            });

            this.isSubmitting = false;
        }
    };

    //------------------------------------------------------
    // Reset search status and result, but does not clear
    // search data
    //------------------------------------------------------
    private readonly _resetResult = (): void => {
        this.result = undefined;
        this.formSuccess = false;
        this.formFailure = false;
    };
}

export default TestLocator;
